import {useEffect, useMemo} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Button, Form, Modal, Spin} from 'antd';
import {useNavigate, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {walletCurrencies} from 'utils/mock';
import {
  GET_PLAN_SUBSCRIPTIONS_CATALOGS,
  GET_SUBSCRIPTION_PLANS_DETAILS,
  UPSERT_SUBSCRIPTION_PLAN_DETAILS,
} from '../resolvers';
import styles from '../subscriptionSettings.module.scss';
import PlanRemindersSection from '../components/planFeatures/PlanRemindersSection';
import {humanizeKey} from '../subscriptions/PlanSubscriptionsHelpers';

const PLANS_ROUTE = '/dashboard/plans/subscription-setting/plans';

const COPY_KEYS = [
  'headerBadgeText',
  'title',
  'subtitle',
  'footerText',
  'primaryCtaLabel',
  'secondaryCtaLabel',
];

const DISCOUNT_TYPES = [
  {value: 'FIXED', label: 'Fixed amount'},
  {value: 'VARIABLE', label: 'Variable (%)'},
];

const localized = (en, fr) => ({EN: en ?? '', FR: fr ?? ''});

const trimmed = value => `${value ?? ''}`.trim();

// Quill emits "<p><br></p>" for an empty rich-text field (e.g. the subtitle);
// treat such markup as blank so an untouched field isn't persisted as non-empty
// HTML — which would otherwise pollute FR multiLang and force an extra save.
const EMPTY_RICH_TEXT = /^(<p>(<br\s*\/?>)?<\/p>)+$/i;
const trimmedCopy = value => {
  const text = trimmed(value);
  return EMPTY_RICH_TEXT.test(text) ? '' : text;
};

const mapPlanToForm = plan => {
  const rs = plan?.reminderSettings || {};
  const frEntry = (plan?.multiLang || []).find(m => m.language === 'FR');
  const frRs = frEntry?.reminderSettings || {};
  // Feature FR labels travel by index, aligned with the base features array.
  const frFeatureLabel = index => frRs.features?.[index]?.label ?? '';

  // FR copy is stored per reminder and keyed by the server-generated reminderId,
  // so match each base reminder to its FR entry by id.
  const frReminderCopy = id =>
    (frRs.reminders || []).find(fr => fr.reminderId === id)?.copyOverrides ||
    {};

  return {
    reminderSettingsEnabled: Boolean(rs.enabled),
    reminderMaxShowCount: rs.maxShowCount ?? 1,
    reminderDismissible: rs.dismissible ?? false,
    reminderAutoCloseSec:
      rs.autoCloseMs != null ? Math.round(rs.autoCloseMs / 1000) : undefined,
    reminderFeatures: (rs.features || []).map((f, index) => ({
      icon: f.icon ?? '',
      label: localized(f.label, frFeatureLabel(index)),
    })),
    reminders: (rs.reminders || []).map(r => {
      const frCopy = frReminderCopy(r.id);
      return {
        id: r.id,
        enabled: r.enabled ?? true,
        daysBeforeExpiry: r.daysBeforeExpiry ?? undefined,
        // Announcement copy lives on each reminder; EN from the base reminder,
        // FR from the matching multiLang reminder entry.
        copy: COPY_KEYS.reduce(
          (acc, key) => ({
            ...acc,
            [key]: localized(r.copyOverrides?.[key], frCopy[key]),
          }),
          {},
        ),
        catalogTabs: (r.catalogTabs || []).map(tab => ({
          id: tab.id,
          catalogKey: tab.catalogKey,
          isDefault: !!tab.isDefault,
          applyDiscount: !!(tab.appliedDiscount && tab.appliedDiscount.length),
          discounts: (tab.appliedDiscount || []).map(d => ({
            type: d.type || 'FIXED',
            value: d.value ?? null,
            currency: d.currency,
          })),
        })),
      };
    }),
  };
};

const buildCopyOverrides = (copy, lang) => {
  const result = {};
  COPY_KEYS.forEach(key => {
    const value = trimmedCopy(copy?.[key]?.[lang]);
    if (value) {
      result[key] = value;
    }
  });
  return result;
};

// Build server catalogTabs from the form rows: drop tabs with no catalog item,
// keep exactly one default (the flagged tab, else the first), and only attach
// appliedDiscount when the tab opts in and has at least one valid currency row.
const buildCatalogTabs = tabs => {
  const valid = (tabs || []).filter(tab => tab && tab.catalogKey);
  const flagged = valid.findIndex(tab => tab.isDefault);
  const defaultIndex = flagged === -1 && valid.length ? 0 : flagged;
  return valid.map((tab, index) => {
    const discounts = (tab.discounts || [])
      .filter(
        d =>
          d &&
          d.currency &&
          d.value !== undefined &&
          d.value !== null &&
          d.value !== '',
      )
      .map(d => ({type: d.type, value: Number(d.value), currency: d.currency}));
    return {
      ...(tab.id && {id: tab.id}),
      catalogKey: tab.catalogKey,
      isDefault: index === defaultIndex,
      appliedDiscount: tab.applyDiscount && discounts.length ? discounts : null,
    };
  });
};

const hasFrContent = values =>
  (values.reminders || []).some(r =>
    COPY_KEYS.some(key => trimmedCopy(r.copy?.[key]?.FR)),
  ) || (values.reminderFeatures || []).some(f => trimmed(f.label?.FR));

const PlanFeaturesPage = () => {
  const navigate = useNavigate();
  const {planKey} = useParams();
  const [form] = Form.useForm();

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Subscription),
  );
  const canSubmit = Boolean(tabOperations?.update);

  const currencyOptions = useMemo(
    () =>
      walletCurrencies.map(currency => ({
        value: currency.key,
        label: `${currency.key} (${currency.symbol})`,
        symbol: currency.symbol,
      })),
    [],
  );

  const {data: plansData, loading: plansLoading} = useQuery(
    GET_SUBSCRIPTION_PLANS_DETAILS,
    {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data: catalogData, loading: catalogLoading} = useQuery(
    GET_PLAN_SUBSCRIPTIONS_CATALOGS,
    {
      skip: !planKey,
      variables: {filter: {plan: planKey}},
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [upsert, {loading: saving}] = useMutation(
    UPSERT_SUBSCRIPTION_PLAN_DETAILS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_TEXT,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const plansResponse = plansData?.getAllSubscriptionPlans;
  const planDetails = useMemo(() => {
    if (plansResponse?.__typename !== 'ResponseWithDabaSubscriptionPlans') {
      return null;
    }
    return (plansResponse.data || []).find(p => p.key === planKey) || null;
  }, [plansResponse, planKey]);

  const catalogResponse = catalogData?.getSubscriptionPlansCatalog;
  const catalogItems =
    catalogResponse?.__typename === 'ResponseWithSubscriptionCatalog'
      ? catalogResponse.data || []
      : [];

  useEffect(() => {
    if (plansLoading) {
      return;
    }
    form.setFieldsValue(mapPlanToForm(planDetails || {}));
  }, [plansLoading, planDetails, form]);

  const handleTranslate = watchName => {
    const sourceText = trimmed(form.getFieldValue([...watchName, 'EN']));
    if (!sourceText) {
      return;
    }
    translateText({
      variables: {sourceLang: 'EN', targetLang: 'FR', sourceText},
    })
      .then(({data}) => {
        const response = data?.translateText;
        if (
          response?.__typename === 'Error' ||
          response?.statusCode === 400 ||
          !response?.targetText
        ) {
          toast.error(response?.message || 'Error translating text');
          return;
        }
        form.setFieldValue([...watchName, 'FR'], response.targetText);
        toast.success('Translated successfully');
      })
      .catch(error => toast.error(error.message || 'Error translating text'));
  };

  const buildBaseInput = values => {
    const autoCloseSec = Number(values.reminderAutoCloseSec);
    return {
      key: planKey,
      name: planDetails?.name,
      ...(planDetails?.description != null && {
        description: planDetails.description,
      }),
      isEnabled: planDetails?.isEnabled ?? true,
      isVisibleToUsers: planDetails?.isVisibleToUsers ?? false,
      reminderSettings: {
        enabled: Boolean(values.reminderSettingsEnabled),
        maxShowCount: Number(values.reminderMaxShowCount) || 1,
        dismissible: Boolean(values.reminderDismissible),
        ...(autoCloseSec > 0 && {autoCloseMs: autoCloseSec * 1000}),
        features: (values.reminderFeatures || []).map(f => ({
          icon: trimmed(f.icon),
          label: trimmed(f.label?.EN),
        })),
        reminders: (values.reminders || []).map(r => {
          const enCopy = buildCopyOverrides(r.copy, 'EN');
          return {
            ...(r.id && {id: r.id}),
            enabled: Boolean(r.enabled),
            daysBeforeExpiry: Number(r.daysBeforeExpiry),
            catalogTabs: buildCatalogTabs(r.catalogTabs),
            ...(Object.keys(enCopy).length && {copyOverrides: enCopy}),
          };
        }),
      },
    };
  };

  const buildFrMultiLang = values => {
    // Feature FR labels travel by index (aligned with the base features array),
    // mirroring how the announcement translates its benefits.
    const features = (values.reminderFeatures || []).map(f => ({
      label: trimmed(f.label?.FR),
    }));
    // FR copy attaches to its reminder by reminderId, so only reminders that
    // already have a server id can carry it. A brand-new reminder's FR copy
    // rides along on the next save, once the upsert has assigned it an id.
    const reminders = (values.reminders || [])
      .filter(r => r.id)
      .map(r => ({
        reminderId: r.id,
        copyOverrides: buildCopyOverrides(r.copy, 'FR'),
      }))
      .filter(r => Object.keys(r.copyOverrides).length);
    const frEntry = (planDetails?.multiLang || []).find(
      m => m.language === 'FR',
    );

    return [
      {
        language: 'FR',
        ...(frEntry?.name && {name: frEntry.name}),
        ...(frEntry?.description && {description: frEntry.description}),
        reminderSettings: {
          features,
          ...(reminders.length && {reminders}),
        },
      },
    ];
  };

  const finish = message => {
    toast.success(message || 'Plan saved successfully');
    navigate(PLANS_ROUTE);
  };

  const performSave = async () => {
    if (!planKey) {
      toast.error('Missing plan key');
      return;
    }

    // getFieldsValue(true) also captures the catalog-tab default/apply switches,
    // which are set programmatically rather than bound to a Form.Item.
    const values = form.getFieldsValue(true);

    try {
      const input = buildBaseInput(values);
      // Shared FR copy + feature labels no longer bind to server-generated ids,
      // so they ride along in a single upsert (no more two-step save).
      if (hasFrContent(values)) {
        input.multiLang = buildFrMultiLang(values);
      }
      const response = await upsert({variables: {input}});
      const result = response?.data?.upsertSubscriptionPlan;
      if (!result || result.__typename === 'Error') {
        toast.error(result?.message || 'Failed to save plan');
        return;
      }
      finish(result.message);
    } catch (error) {
      toast.error(error.message || 'Failed to save plan');
    }
  };

  const handleSubmit = () => {
    Modal.confirm({
      title: 'Update plan features & reminders?',
      content:
        'Please confirm the features, discounts, and translations are correct before saving.',
      okText: 'Update',
      cancelText: 'Cancel',
      centered: true,
      onOk: () => performSave(),
    });
  };

  const loading = plansLoading || catalogLoading;
  const planTitle = planDetails?.name || humanizeKey(planKey);

  return (
    <div className={styles['subscriptions-page']}>
      <div className={styles['subscriptions-toolbar']}>
        <div className={styles['subscriptions-header']}>
          <Button type="default" onClick={() => navigate(PLANS_ROUTE)}>
            Back to Plans
          </Button>
          <div className={styles['subscriptions-title-block']}>
            <h1 className={styles['subscriptions-title']}>
              {planTitle} · Features & Reminders
            </h1>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spin size="large" />
          <h3>Loading plan...</h3>
        </div>
      ) : !planDetails ? (
        <div className={styles['empty-state']}>Plan not found.</div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={() => toast.error('Fix validation errors')}>
          <PlanRemindersSection
            form={form}
            catalogItems={catalogItems}
            currencyOptions={currencyOptions}
            discountTypes={DISCOUNT_TYPES}
            onTranslate={handleTranslate}
            loadingTranslation={loadingTranslation}
          />
          <div style={{marginTop: 16}}>
            <SubmitButton disabled={saving || !canSubmit} label="Save" />
          </div>
        </Form>
      )}
    </div>
  );
};

export default PlanFeaturesPage;
